<template>
  <v-dialog v-model="showDialog" max-width="600" persistent>
    <v-card>
      <!-- Header -->
      <v-card-title class="d-flex align-center px-6 py-4 primaryDialogTitle">
        <span class="text-h5">Booking Details</span>
        <v-chip v-if="bookingData && isNewBooking" color="error" small label class="ml-2">
          New
        </v-chip>
        <v-spacer />
        <!-- Status Badges -->
        <template v-if="bookingData">
          <!-- Approval Status Chip -->
          <v-chip
            v-if="approvalStatusLabel"
            :color="approvalStatusColor"
            text-color="black"
            small
            label
            class="mr-2"
          >
            {{ approvalStatusLabel }}
          </v-chip>
          <!-- Booking Status Chip -->
          <v-chip
            v-if="bookingStatusLabel"
            :color="bookingStatusColor"
            text-color="black"
            small
            label
            :outlined="isCompleted"
          >
            {{ bookingStatusLabel }}
          </v-chip>
        </template>
        <v-btn icon @click="closeDialog" class="ml-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="pa-6">
        <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4" />

        <template v-if="bookingData && !isLoading">
          <!-- Clinic Row -->
          <v-row>
            <v-col cols="12">
              <div class="text-caption grey--text text--darken-1 mb-1">Clinic</div>
              <div class="d-flex align-center">
                <v-icon small class="mr-2">mdi-hospital</v-icon>
                <span>{{ clinicName }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- Service and Doctor Row -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Service</div>
              <div class="d-flex align-center">
                <v-icon small class="mr-2" v-if="bookingData.serviceDetails.service.mdiIcon">
                  {{ bookingData.serviceDetails.service.mdiIcon }}
                </v-icon>
                <span>{{ serviceName }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Doctor</div>
              <div>{{ doctorName }}</div>
            </v-col>
          </v-row>

          <!-- Patient First Name and Last Name Row -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">First Name</div>
              <div>{{ patientFirstName }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Last Name</div>
              <div>{{ patientLastName }}</div>
            </v-col>
          </v-row>

          <!-- Contact Number and Age Row -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Contact No.</div>
              <div>{{ contactNumber }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Age</div>
              <div>{{ patientAge }}</div>
            </v-col>
          </v-row>

          <!-- Date and Time Row -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Booked Date</div>
              <div>{{ bookedDate }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Booked Time</div>
              <div>{{ bookedTime }}</div>
            </v-col>
          </v-row>

          <!-- Absent Time and Booking No Row -->
          <v-row class="mt-4">
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Absent Time:</div>
              <div>{{ absentTime }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption grey--text text--darken-1 mb-1">Booking No.</div>
              <div>#{{ bookingNumber }}</div>
            </v-col>
          </v-row>
        </template>

        <!-- Empty State -->
        <v-container v-if="!bookingData && !isLoading" class="text-center py-8">
          <v-icon size="64" color="grey">mdi-calendar-blank</v-icon>
          <div class="text-h6 grey--text mt-4">No booking data available</div>
        </v-container>
      </v-card-text>

      <v-divider v-if="bookingData && !isLoading" />

      <!-- Actions -->
      <v-card-actions v-if="bookingData && !isLoading" class="pa-6 pt-4">
        <v-row>
          <!-- Pending Status Actions -->
          <template v-if="isPending">
            <v-col cols="6">
              <v-btn
                block
                large
                rounded
                color="error"
                dark
                @click="rejectBooking"
                :loading="isRejecting"
              >
                Reject Booking
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                large
                rounded
                color="primary"
                dark
                @click="approveBooking"
                :loading="isApproving"
              >
                Approve Booking
              </v-btn>
            </v-col>
          </template>

          <!-- Approved + Created Status Actions -->
          <template v-else-if="isApproved && isCreated">
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="error"
                dark
                @click="cancelBooking"
                :loading="isCancelling"
              >
                Cancel
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="warning"
                dark
                @click="markAbsent"
                :loading="isMarkingAbsent"
              >
                Mark Absent
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="primary"
                dark
                @click="markArrived"
                :loading="isMarkingArrived"
              >
                Mark Arrived
              </v-btn>
            </v-col>
          </template>

          <!-- Approved + Arrived Status Actions -->
          <template v-else-if="isApproved && isArrived">
            <v-col cols="6">
              <v-btn
                block
                large
                rounded
                color="error"
                dark
                @click="markAbsent"
                :loading="isMarkingAbsent"
              >
                Mark Absent
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                block
                large
                rounded
                color="primary"
                dark
                @click="markCompleted"
                :loading="isMarkingCompleted"
              >
                Complete
              </v-btn>
            </v-col>
          </template>

          <!-- Approved + Absent Status Actions -->
          <template v-else-if="isApproved && isAbsent">
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="error"
                dark
                @click="cancelBooking"
                :loading="isCancelling"
              >
                Cancel
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="warning"
                dark
                @click="markCompleted"
                :loading="isMarkingCompleted"
              >
                Complete
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                large
                rounded
                color="primary"
                dark
                @click="markArrived"
                :loading="isMarkingArrived"
              >
                Mark Arrived
              </v-btn>
            </v-col>
          </template>

          <!-- Other Status - Close Only -->
          <template v-else>
            <v-col>
              <v-btn block large rounded color="primary" @click="closeDialog"> Close </v-btn>
            </v-col>
          </template>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import api from '@/api';
import { parseLocalizedString } from '@/services/utils';
import {
  BookingStatus,
  BookingApprovalStatus,
  readBooking,
  updateBookingStatus,
} from '@/api/bookings';
import { syncClinicUnreadBookingCount } from '@/plugins/app-state';
import { RESTFUL } from '@/data/constants';
import { crudRead } from '@/api/crud';

export default {
  name: 'BookingDetailsDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    booking: {
      type: Object,
      default: null,
    },
    timetableData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showDialog: false,
      bookingData: null,
      isLoading: false,
      isApproving: false,
      isRejecting: false,
      isCancelling: false,
      isMarkingAbsent: false,
      isMarkingArrived: false,
      isMarkingCompleted: false,
      locale: 'en',
      absentCount: 0,
      loadingAbsentCount: false,
      lastLoadedBookingId: null, // Track last loaded booking to prevent duplicates
      clinicData: null, // Store fetched clinic data separately
      loadingClinic: false, // Track clinic loading state
    };
  },
  computed: {
    ...mapGetters('auth', ['isClinicStaff']),

    isNewBooking() {
      // Check if booking has not been read by clinic staff yet
      return !this.bookingData?.clinicReadAt;
    },

    isPending() {
      return (
        this.bookingData?.approvalStatus === BookingApprovalStatus.pending ||
        this.bookingData?.approvalStatus === 'pending'
      );
    },

    isApproved() {
      return (
        this.bookingData?.approvalStatus === BookingApprovalStatus.approved ||
        this.bookingData?.approvalStatus === 'approved'
      );
    },

    isCreated() {
      return this.bookingData?.status === BookingStatus.created;
    },

    isArrived() {
      return this.bookingData?.status === BookingStatus.arrived;
    },

    isAbsent() {
      return this.bookingData?.status === BookingStatus.absent;
    },

    isCompleted() {
      return this.bookingData?.status === BookingStatus.completed;
    },

    isCancelled() {
      return this.bookingData?.status === BookingStatus.cancelled;
    },

    // Approval Status (separate chip)
    approvalStatusLabel() {
      if (this.isPending) return 'Pending';
      if (this.isApproved) return 'Approved';
      if (this.bookingData?.approvalStatus === BookingApprovalStatus.unapproved)
        return 'Unapproved';
      return null; // Don't show chip if no approval status
    },

    approvalStatusColor() {
      if (this.isPending) return '#FFF7DC'; // Pending: light yellow
      if (this.isApproved) return '#DCEFFF'; // Approved: light blue
      return '#C4C4C4'; // Unapproved: grey
    },

    // Booking Status (separate chip)
    bookingStatusLabel() {
      if (this.bookingData?.status === BookingStatus.created) return null; // Don't show "Created" status
      if (this.bookingData?.status === BookingStatus.arrived) return 'Arrived';
      if (this.bookingData?.status === BookingStatus.absent) return 'Absent';
      if (this.bookingData?.status === BookingStatus.completed) return 'Completed';
      if (this.bookingData?.status === BookingStatus.cancelled) return 'Cancelled';
      return 'Unknown';
    },

    bookingStatusColor() {
      if (this.bookingData?.status === BookingStatus.created) return '#DCEFFF'; // Created: light blue
      if (this.bookingData?.status === BookingStatus.arrived) return '#DCF2DE'; // Arrived: light green
      if (this.bookingData?.status === BookingStatus.absent) return '#FFC5C5'; // Absent: light red
      if (this.bookingData?.status === BookingStatus.completed) return '#FFFFFF'; // Completed: white
      if (this.bookingData?.status === BookingStatus.cancelled) return '#C4C4C4'; // Cancelled: grey
      return '#C4C4C4';
    },

    serviceName() {
      const service = this.bookingData?.serviceDetails?.service;
      if (!service?.name) return 'General Practice';
      const parsed = parseLocalizedString(service.name);
      return parsed[this.locale] || parsed.en || 'General Practice';
    },

    doctorName() {
      const doctor = this.bookingData?.serviceDetails?.doctor;
      if (!doctor) return 'Not Assigned';

      const firstName = parseLocalizedString(doctor.firstName || '');
      const lastName = parseLocalizedString(doctor.lastName || '');

      const first = firstName[this.locale] || firstName.en || '';
      const last = lastName[this.locale] || lastName.en || '';

      return `${last} ${first}`.trim() || 'Not Assigned';
    },

    clinicName() {
      const clinic = this.clinicData || this.bookingData?.clinic;
      if (!clinic) return this.loadingClinic ? 'Loading...' : 'N/A';
      const name = parseLocalizedString(clinic.name);
      return name[this.locale] || name.en || 'N/A';
    },

    patientName() {
      return (
        this.bookingData?.serviceDetails?.patient?.name ||
        this.bookingData?.created_by?.name ||
        'Unknown Patient'
      );
    },

    patientFirstName() {
      const patient = this.bookingData?.serviceDetails?.patient;
      if (!patient) return 'N/A';

      return patient.name_first || patient.name || 'N/A';
    },

    patientLastName() {
      const patient = this.bookingData?.serviceDetails?.patient;
      if (!patient) return 'N/A';

      return patient.name_last || 'N/A';
    },

    patientAge() {
      const patient = this.bookingData?.serviceDetails?.patient;
      if (!patient) return 'N/A';

      return patient.age || 'N/A';
    },

    contactNumber() {
      return (
        this.bookingData?.serviceDetails?.contactNumber ||
        this.bookingData?.serviceDetails?.patient?.contactNumber ||
        this.bookingData?.created_by?.phone ||
        'N/A'
      );
    },

    bookedDate() {
      const time = this.timetableData?.time?.start || this.bookingData?.time?.start;
      if (!time) return 'N/A';
      return moment(time).format('DD-MMM-YYYY');
    },

    bookedTime() {
      const start = this.timetableData?.time?.start || this.bookingData?.time?.start;
      const end = this.timetableData?.time?.end || this.bookingData?.time?.end;
      if (!start || !end) return 'N/A';
      return `${moment(start).format('HH:mm')} — ${moment(end).format('HH:mm')}`;
    },

    absentTime() {
      return this.loadingAbsentCount ? 'Loading...' : String(this.absentCount);
    },

    bookingNumber() {
      // Extract booking number from ticketNumber or use ID
      const ticketNumber = this.bookingData?.ticketNumber;
      if (ticketNumber) {
        const parts = ticketNumber.split('-');
        return parts[parts.length - 1] || this.bookingData?.id?.slice(-4) || '0000';
      }
      return this.bookingData?.id?.slice(-4) || '0000';
    },
  },
  watch: {
    value: {
      handler(val) {
        this.showDialog = val;
        if (val) {
          this.loadBookingData();
        }
      },
      immediate: true,
    },
    showDialog(val) {
      this.$emit('input', val);
    },
    booking: {
      handler() {
        if (this.showDialog) {
          this.loadBookingData();
        }
      },
      deep: true,
    },
  },
  methods: {
    loadBookingData() {
      // Set loading state if we don't have booking data yet
      if (!this.booking && !this.timetableData?.booking && !this.id) {
        this.isLoading = true;
        this.bookingData = null;
        this.lastLoadedBookingId = null;
        return;
      }

      // Determine the booking ID
      const bookingId =
        this.booking?._id ||
        this.booking?.id ||
        this.timetableData?.booking?._id ||
        this.timetableData?.booking?.id ||
        this.id;

      // Prevent duplicate loading of the same booking
      if (this.lastLoadedBookingId === bookingId) {
        return;
      }

      this.lastLoadedBookingId = bookingId;

      // Use provided booking data or timetable data
      if (this.booking) {
        this.bookingData = this.booking;
        this.isLoading = false;
        this.loadAbsentCount();
        this.loadClinicIfNeeded();
        this.markBookingAsRead();
      } else if (this.timetableData?.booking) {
        this.bookingData = this.timetableData.booking;
        this.isLoading = false;
        this.loadAbsentCount();
        this.loadClinicIfNeeded();
        this.markBookingAsRead();
      } else if (this.id) {
        // Load booking data from API using ID
        this.loadBookingFromAPI();
      }
    },

    async loadBookingFromAPI() {
      if (!this.id) {
        return;
      }

      this.isLoading = true;
      try {
        const booking = await readBooking(api, this.id, {
          populate: [
            {
              path: 'clinic',
              select: ['name'],
            },
          ],
        });
        this.bookingData = booking;
        this.loadAbsentCount();
        this.markBookingAsRead();
      } catch (error) {
        console.error('Failed to load booking:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to load booking details',
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async loadAbsentCount() {
      // Get phone number from booking data
      const phoneNumber = this.contactNumber;

      if (!phoneNumber || phoneNumber === 'N/A') {
        this.absentCount = 0;
        return;
      }

      this.loadingAbsentCount = true;
      try {
        const response = await api.get('rml/admin/booking/absentCount', {
          params: {
            phoneNumbers: [phoneNumber],
          },
        });

        // Response format: [{"phoneNumber":"+85280008023","count":1}]
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.absentCount = response.data[0].count || 0;
        } else {
          this.absentCount = 0;
        }
      } catch (error) {
        console.error('Failed to load absent count:', error);
        this.absentCount = 0;
      } finally {
        this.loadingAbsentCount = false;
      }
    },

    async loadClinicIfNeeded() {
      // Check if clinic data already exists in bookingData as a populated object
      // If clinic is an object with name property, it's already populated
      if (
        this.bookingData?.clinic &&
        typeof this.bookingData.clinic === 'object' &&
        this.bookingData.clinic.name
      ) {
        this.clinicData = null;
        return;
      }

      // Get clinic ID from booking data - could be a string ID or object reference
      let clinicRefId = null;

      if (typeof this.bookingData?.clinic === 'string') {
        clinicRefId = this.bookingData.clinic;
      } else if (
        this.bookingData?.clinic &&
        typeof this.bookingData.clinic === 'object' &&
        (this.bookingData.clinic._id || this.bookingData.clinic.id)
      ) {
        // Clinic is an object but might not be fully populated, get its ID
        clinicRefId = this.bookingData.clinic._id || this.bookingData.clinic.id;
      } else {
        // Look for clinic ID in other possible locations
        clinicRefId =
          this.bookingData?.clinicId ||
          this.bookingData?.clinic_id ||
          this.bookingData?.serviceDetails?.clinicId;
      }

      if (!clinicRefId) {
        this.clinicData = null;
        return;
      }

      // Prevent duplicate loading
      if (this.clinicData?._id === clinicRefId || this.clinicData?.id === clinicRefId) {
        return;
      }

      this.loadingClinic = true;
      try {
        const response = await crudRead(api, RESTFUL.clinicProfile.read, clinicRefId, {
          select: ['name'],
        });
        this.clinicData = response;
      } catch (error) {
        console.error('Failed to load clinic data:', error);
        this.clinicData = null;
      } finally {
        this.loadingClinic = false;
      }
    },

    closeDialog() {
      this.showDialog = false;
      this.lastLoadedBookingId = null; // Reset tracking when dialog closes
      this.clinicData = null; // Reset clinic data
      this.$emit('close');
    },

    async markBookingAsRead() {
      // Only mark as read if user is clinic staff
      if (!this.isClinicStaff) {
        return;
      }

      // Only mark as read if it hasn't been read yet
      if (!this.bookingData || this.bookingData.clinicReadAt) {
        return;
      }

      const bookingId = this.bookingData.id || this.bookingData._id;
      if (!bookingId) return;

      try {
        // Update clinicReadAt to current time
        await api.patch(`rml/admin/bookings/${bookingId}`, {
          clinicReadAt: new Date(),
        });

        // Update local booking data
        this.bookingData.clinicReadAt = new Date();
        await syncClinicUnreadBookingCount(this.$store, api);
        // Emit update event so parent components can refresh
        this.$emit('update', this.bookingData);
      } catch (error) {
        console.error('Failed to mark booking as read:', error);
        // Silently fail - this is not critical functionality
      }
    },

    async approveBooking() {
      this.isApproving = true;
      try {
        const updatedBooking = await updateBookingStatus(
          api,
          this.bookingData.id || this.bookingData._id,
          {
            approvalStatus: BookingApprovalStatus.approved,
          },
        );

        this.bookingData = updatedBooking;

        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Booking approved successfully',
          type: 'success',
          color: 'success',
        });

        this.$emit('update', this.bookingData);
      } catch (error) {
        console.error('Failed to approve booking:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to approve booking',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isApproving = false;
      }
    },

    async rejectBooking() {
      this.isRejecting = true;
      try {
        const updatedBooking = await updateBookingStatus(api, this.bookingData.id, {
          approvalStatus: BookingApprovalStatus.unapproved,
          status: BookingStatus.cancelled,
        });

        this.bookingData = updatedBooking;

        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Booking rejected',
          type: 'success',
          color: 'success',
        });

        this.$emit('update', this.bookingData);
        this.closeDialog();
      } catch (error) {
        console.error('Failed to reject booking:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to reject booking',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isRejecting = false;
      }
    },

    async cancelBooking() {
      // Show confirmation dialog
      this.$root.$confirm
        .open('Confirm Cancel', 'Are you sure you want to cancel this booking?', {
          color: 'warning',
        })
        .then(async confirm => {
          if (confirm) {
            this.isCancelling = true;
            try {
              const updatedBooking = await updateBookingStatus(api, this.bookingData.id, {
                status: BookingStatus.cancelled,
              });

              this.bookingData = updatedBooking;

              this.$store.dispatch('alert/updateAlertMessage', {
                msg: 'Booking cancelled',
                type: 'success',
                color: 'success',
              });

              this.$emit('cancel', this.bookingData);
              this.closeDialog();
            } catch (error) {
              console.error('Failed to cancel booking:', error);
              this.$store.dispatch('alert/updateAlertMessage', {
                msg: 'Failed to cancel booking',
                type: 'error',
                color: 'error',
              });
            } finally {
              this.isCancelling = false;
            }
          }
        });
    },

    async markAbsent() {
      this.isMarkingAbsent = true;
      try {
        const updatedBooking = await updateBookingStatus(api, this.bookingData.id, {
          status: BookingStatus.absent,
        });

        this.bookingData = updatedBooking;

        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Marked as absent',
          type: 'success',
          color: 'success',
        });

        this.$emit('update', this.bookingData);
        this.closeDialog();
      } catch (error) {
        console.error('Failed to mark absent:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to mark as absent',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isMarkingAbsent = false;
      }
    },

    async markArrived() {
      this.isMarkingArrived = true;
      try {
        const updatedBooking = await updateBookingStatus(api, this.bookingData.id, {
          status: BookingStatus.arrived,
        });

        this.bookingData = updatedBooking;

        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Marked as arrived',
          type: 'success',
          color: 'success',
        });

        this.$emit('update', this.bookingData);
        this.closeDialog();
      } catch (error) {
        console.error('Failed to mark arrived:', error);
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to mark as arrived',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isMarkingArrived = false;
      }
    },

    async markCompleted() {
      // Show confirmation dialog
      this.$root.$confirm
        .open('Confirm Complete', 'Are you sure you want to mark this booking as completed?', {
          color: 'success',
        })
        .then(async confirm => {
          if (confirm) {
            this.isMarkingCompleted = true;
            try {
              const updatedBooking = await updateBookingStatus(api, this.bookingData.id, {
                status: BookingStatus.completed,
              });

              this.bookingData = updatedBooking;

              this.$store.dispatch('alert/updateAlertMessage', {
                msg: 'Booking completed successfully',
                type: 'success',
                color: 'success',
              });

              this.$emit('update', this.bookingData);
              this.closeDialog();
            } catch (error) {
              console.error('Failed to mark completed:', error);
              this.$store.dispatch('alert/updateAlertMessage', {
                msg: 'Failed to mark as completed',
                type: 'error',
                color: 'error',
              });
            } finally {
              this.isMarkingCompleted = false;
            }
          }
        });
    },
  },
};
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}
</style>
